import type { Progress } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useCallback, useRef } from 'react'

const DEBOUNCE_MS = 1000

export function useMangaProgress() {
  const user = useAuthStore((s) => s.user)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveProgress = useCallback(
    (titleId: string, chapterId: string, page: number) => {
      if (!user) return

      if (timerRef.current) clearTimeout(timerRef.current)

      timerRef.current = setTimeout(async () => {
        await supabase.from('progress').upsert(
          {
            user_id: user.id,
            title_id: titleId,
            chapter_id: chapterId,
            page,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,title_id' },
        )
      }, DEBOUNCE_MS)
    },
    [user],
  )

  const saveProgressImmediate = useCallback(
    async (titleId: string, chapterId: string, page: number) => {
      if (!user) return null
      if (timerRef.current) clearTimeout(timerRef.current)

      return supabase.from('progress').upsert(
        {
          user_id: user.id,
          title_id: titleId,
          chapter_id: chapterId,
          page,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,title_id' },
      )
    },
    [user],
  )

  const getProgress = useCallback(
    async (titleId: string): Promise<Progress | null> => {
      if (!user) return null
      const { data } = await supabase
        .from('progress')
        .select('*')
        .eq('title_id', titleId)
        .maybeSingle()
      return data
    },
    [user],
  )

  return { saveProgress, saveProgressImmediate, getProgress }
}
